import type { OrgSyncResults } from '@magicschool/business-logic/edlink-sync';
import type { MagicSchoolUser } from '@magicschool/supabase/user';
import { createStoreSlice, type SetField } from 'features/store/zustand';
import { getLatestOrgSyncResults } from '@/app/_actions/orgs/syncs/getLatestOrgSyncResults';
import { renderDoWMDYT } from '@/util/date';

export type OrgSyncStore = {
  loading: boolean;
  orgId: string | null;
  lastSynced: string;
  lastSyncHash: string | null;
  syncDetailsModalOpen: boolean;
  load: (user: MagicSchoolUser, locale: string) => void;
  open: (orgId: string | null | undefined) => void;
  setField: SetField<OrgSyncStore>;
};

const defaultState = {
  loading: true,
  orgId: null,
  lastSynced: '',
  lastSyncHash: null,
  syncDetailsModalOpen: false,
};

export const createOrgSyncStoreSlice = createStoreSlice('OrgSyncStoreData', defaultState, ({ get, set, setField }) => ({
  setField,
  open: (orgId) => {
    set({ orgId, syncDetailsModalOpen: true });
  },
  load: async (_, locale) => {
    const orgId = get().orgId;
    if (!orgId) {
      return;
    }

    const orgSync = await getLatestOrgSyncResults(orgId, 'full');

    // The action returns null when the org has no completed sync yet, and an AuthorizationError
    // when the user can't read them. Errors don't survive the server-action boundary as class
    // instances, so key off the presence of `orgSyncId` rather than an `instanceof` check.
    const orgSyncData = orgSync.data as OrgSyncResults | null | undefined;
    if (!orgSyncData?.orgSyncId) {
      set({ loading: false, lastSynced: '', lastSyncHash: null });
      return;
    }

    set({
      loading: false,
      lastSynced: orgSyncData.finish_date ? renderDoWMDYT(locale, orgSyncData.finish_date) : '',
      lastSyncHash: orgSyncData.orgSyncId,
    });
  },
}));
